import React, { useMemo, useState } from 'react';
import EmployeeTenureChart from './employeeTenure/EmployeeTenureChart';
import { Grid2 as Grid, IconButton, Collapse, Box, Typography } from '@mui/material';
import StaffingRateChart from './staffingRateOverProject/StaffingRateChart';
import ExpandIcon from '@mui/icons-material/Expand';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { useShowContext } from 'framework/show';
import { StaffingDataType } from 'domain/shared/staffing';
import AverageTenureChart from './averageTenure/AverageTenureChart';
import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';

const TrendsWrapper = () => {
  const { data } = useShowContext<StaffingDataType>();
  const {
    teamChart: { fteRate = [], tenureDistribution = [], averageTenure = [] },
  } = data.getData();

  const [expandedChart, setExpandedChart] = useState<Nullable<string>>(null);

  const fteResultRage = useMemo(() => {
    return fteRate.map(item => ({
      ...item,
      startDate: moment(item.startDate).format(DATE_FORMAT.MONTH),
    }));
  }, [fteRate]);

  const averageTenureResultRage = useMemo(() => {
    return averageTenure.map(item => ({
      ...item,
      endMonth: moment(item.endMonth).format(DATE_FORMAT.MONTH),
    }));
  }, [averageTenure]);

  const handleToggleChart = (chartId: string) => {
    if (expandedChart === chartId) {
      setExpandedChart(null);
    } else {
      setExpandedChart(chartId);
    }
  };

  const charts = useMemo(
    () => [
      {
        id: 'staffing-rate',
        title: 'Staffing Rate Over Project Duration',
        component: <StaffingRateChart data={fteResultRage} />,
        isExpandable: fteResultRage.length > 12,
      },
      {
        id: 'employee-tenure',
        title: 'Worker Tenure Distribution on Project',
        component: <EmployeeTenureChart data={tenureDistribution} />,
        isExpandable: false,
      },
      {
        id: 'average-tenure',
        title: 'Average Tenure Trend in the Project Team Over Time',
        component: <AverageTenureChart data={averageTenureResultRage} />,
        isExpandable: averageTenureResultRage.length > 12,
      },
    ],
    [fteResultRage, tenureDistribution, averageTenureResultRage],
  );

  return (
    <Grid container columnSpacing={2} py={2}>
      {charts.map(chart => {
        const isExpandable = chart.isExpandable ?? true;
        const isExpanded = expandedChart === chart.id;
        const isCollapsed = expandedChart && !isExpanded;

        return (
          <Grid
            key={chart.id}
            size={isExpanded ? 12 : isCollapsed ? 0 : 4}
            sx={{
              transition: 'all 0.3s ease-in-out',
              overflow: 'hidden',
              minHeight: !expandedChart ? 'auto' : isExpanded ? 'auto' : 0,
            }}
          >
            <Collapse
              in={!isCollapsed}
              timeout={300}
              sx={{
                height: '100%',
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  position: 'relative',
                  bgcolor: 'background.paper',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                  }}
                >
                  <Typography width="100%" display="block" textAlign="center" variant="body1">
                    {chart.title}
                  </Typography>

                  {isExpandable && (
                    <IconButton
                      onClick={() => handleToggleChart(chart.id)}
                      size="small"
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(45deg)',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          transform: isExpanded ? 'rotate(0deg) scale(1.1)' : 'rotate(45deg) scale(1.1)',
                        },
                      }}
                    >
                      {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                    </IconButton>
                  )}
                </Box>

                <Box
                  sx={{
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {React.cloneElement(chart.component, { isExpanded })}
                </Box>
              </Box>
            </Collapse>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TrendsWrapper;
